import { ShadowContainerProvider, Toaster } from '@skin-seoul/design-system';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type ThemeProviderProps = {
  children: React.ReactNode;
};

// Pre-compiled Tailwind CSS injected at build time via tsup define
declare const SHORTCODE_COMPILED_CSS: string;

/**
 * Processes Tailwind v4 CSS for Shadow DOM compatibility.
 *
 * Issues with Tailwind v4 CSS inside Shadow DOM:
 * 1. @property declarations are document-scoped only (CSS spec) — Shadow DOM ignores them
 * 2. :root selectors don't match shadow tree elements — need :host instead
 * 3. @property initial-value with inherits:false don't cascade into Shadow DOM
 */
function processCSSForShadowDOM(rawCSS: string) {
  const propertyBlocks: string[] = [];
  const hostFallbacks: string[] = [];

  // 1. Extract @property blocks → register globally, collect initial values for :host
  const cssWithoutProperties = rawCSS.replace(
    /@property\s+(--[\w-]+)\s*\{([^}]*)\}/g,
    (_match, propName: string, body: string) => {
      propertyBlocks.push(`@property ${propName} {${body}}`);

      const initialValueMatch = body.match(/initial-value:\s*([^;]+)/);
      if (initialValueMatch) {
        hostFallbacks.push(`${propName}: ${initialValueMatch[1].trim()}`);
      }

      return '';
    },
  );

  // 2. Replace :root with :host (Tailwind @theme already emits :root,:host — deduplicate)
  const shadowCSS = cssWithoutProperties.replace(/:root\s*,\s*:host/g, ':host').replace(/:root/g, ':host');

  // 3. Build :host fallback for @property initial values (inherits:false = no cascade)
  const hostFallbackCSS = hostFallbacks.length > 0 ? `:host { ${hostFallbacks.join('; ')} }` : '';

  return { shadowCSS, globalPropertyCSS: propertyBlocks.join('\n'), hostFallbackCSS };
}

// Register @property rules globally (document scope) — only once across all instances
let globalPropertiesRegistered = false;

function registerGlobalProperties(css: string) {
  if (globalPropertiesRegistered || !css) {
    return;
  }

  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
  } catch {
    const style = document.createElement('style');
    style.setAttribute('data-skin-seoul-properties', '');
    style.textContent = css;
    document.head.appendChild(style);
  }

  globalPropertiesRegistered = true;
}

// Cache processed stylesheet — shared across all shortcode instances
let cachedShadowSheet: CSSStyleSheet | null = null;
let cssProcessed = false;

function getOrCreateShadowSheet(): CSSStyleSheet | null {
  if (cachedShadowSheet) {
    return cachedShadowSheet;
  }
  if (cssProcessed) {
    return null;
  }

  cssProcessed = true;
  const rawCSS = typeof SHORTCODE_COMPILED_CSS !== 'undefined' ? SHORTCODE_COMPILED_CSS : '';
  if (!rawCSS) {
    return null;
  }

  const { shadowCSS, globalPropertyCSS, hostFallbackCSS } = processCSSForShadowDOM(rawCSS);

  // Step 1: Register @property declarations globally
  registerGlobalProperties(globalPropertyCSS);

  // Step 2: Create shadow stylesheet with processed CSS
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(shadowCSS + '\n' + hostFallbackCSS);
    cachedShadowSheet = sheet;
    return sheet;
  } catch {
    return null;
  }
}

/**
 * Renders children into a Shadow DOM via createPortal.
 *
 * CRITICAL: Uses createPortal (NOT createRoot) so that React context from
 * parent providers (QueryClientProvider, RegionCurrencyContextProvider, etc.)
 * is preserved inside the Shadow DOM. createRoot would create a separate
 * React tree, losing all context — causing components that depend on
 * useUser, useRegionCurrency, useQuery, etc. to silently fail.
 */
export function DesignSystemShadowProvider({ children }: ThemeProviderProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const hostElement = containerRef.current;

    let shadowRoot = hostElement.shadowRoot;
    if (!shadowRoot) {
      shadowRoot = hostElement.attachShadow({ mode: 'open' });
    }

    // Inject Tailwind CSS into Shadow DOM
    const sheet = getOrCreateShadowSheet();
    if (sheet) {
      // adoptedStyleSheets: parsed once, shared across all instances (best performance)
      shadowRoot.adoptedStyleSheets = [sheet];
    } else {
      // Fallback: <style> tag
      const rawCSS = typeof SHORTCODE_COMPILED_CSS !== 'undefined' ? SHORTCODE_COMPILED_CSS : '';
      const { shadowCSS, globalPropertyCSS, hostFallbackCSS } = processCSSForShadowDOM(rawCSS);
      registerGlobalProperties(globalPropertyCSS);
      const styleEl = document.createElement('style');
      styleEl.textContent = shadowCSS + '\n' + hostFallbackCSS;
      shadowRoot.appendChild(styleEl);
    }

    // Clone library-injected styles (Vaul drawer animations) into Shadow DOM
    // Only clone styles containing data-vaul selectors — skip page styles
    // that would override Tailwind utilities (e.g., test.html button styles)
    document.querySelectorAll('head style').forEach((style) => {
      if (style.textContent?.includes('data-vaul')) {
        const clone = document.createElement('style');
        clone.textContent = style.textContent;
        shadowRoot!.appendChild(clone);
      }
    });

    // Create a mount point inside the shadow root for the portal
    const rootEl = document.createElement('div');
    shadowRoot.appendChild(rootEl);

    // Trigger portal rendering by setting the target
    setPortalTarget(rootEl);

    return () => {
      setPortalTarget(null);
      rootEl.remove();
    };
  }, []);

  // Raise shadow host z-index only when Drawer/Dialog is open
  const setOverlayActive = useCallback((active: boolean) => {
    if (containerRef.current) {
      if (active) {
        containerRef.current.style.position = 'relative';
        containerRef.current.style.zIndex = '1400';
      } else {
        containerRef.current.style.position = '';
        containerRef.current.style.zIndex = '';
      }
    }
  }, []);

  const contextValue = useMemo(() => ({ container: portalTarget, setOverlayActive }), [portalTarget, setOverlayActive]);

  return (
    <div ref={containerRef}>
      {portalTarget &&
        createPortal(
          <ShadowContainerProvider value={contextValue}>
            {children}
            <Toaster />
          </ShadowContainerProvider>,
          portalTarget,
        )}
    </div>
  );
}
